import { apiClient } from './client';

/**
 * Wire shape for ROADMAP #43.1 phase 1B:
 *   - `GET /api/v1/sessions/me/cognitive`              (participant cross-tenant)
 *   - `GET /api/v1/tenants/{id}/cognitive`             (tenant_admin Dashboard)
 *   - `GET /api/v1/tenants/{id}/sessions/{sid}/cognitive` (tenant_admin detail)
 */

export interface CognitiveScore {
  id: string;
  session_id: string;
  phase: string; // 'baseline' | 'task' | future
  focus: number | null;
  engagement: number | null;
  valence: number | null;
  workload: number | null;
  computed_by_version: string;
  scored_at: string;
  started_at: string | null;
  procedure_name: string | null;
  /** ROADMAP #44 — maps to public.master_baselines.procedure_group
   *  so the participant grid can resolve the matching faculty /
   *  experienced-cohort reference. Null = no master mapping or
   *  procedure unassigned. */
  procedure_group?: string | null;
  participant_user_id: string | null;
  participant_label: string | null;
  /** Originating tenant (ROADMAP #54) — drives the participant
   *  Dashboard's this-tenant/all-tenants filter. */
  tenant_id?: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  /** Per-window time-series — tenant cognitive_scores revision 16.
   *  Arrays are aligned: index i corresponds to the window centered
   *  at `(i * step_s + window_s/2)` seconds from the phase start.
   *  Null on rows scored before revision 16 — the participant
   *  Dashboard's cognitive-trace chart degrades to a flat-line render
   *  using the per-phase aggregates above when this is missing. */
  trace?: CognitiveTrace | null;
  /** Per-marker audio paraphrase labels (ROADMAP
   *  TRACE-MARKER-LABELS, per-tenant revision 22). Map keyed by
   *  session-elapsed `time_s` (to 2-decimal precision string) →
   *  `{headline, summary, transcript}`. Null when the marker_labels
   *  worker hasn't run yet (feature off, missing API keys, no
   *  anchor uploaded, or scored before this revision). Surfaces on
   *  the Cognitive trace card as headlines above each vertical
   *  marker + summary/transcript in the hover preview. */
  marker_labels?: Record<string, MarkerLabel> | null;
  /** COGNITIVE-TRACE-SMOOTHING: the active smoothing interval (seconds) a
   *  tenant admin applied to this session's trace. NULL / 0 = the native
   *  1-second series. `trace` already reflects the binning; the tenant
   *  dashboard seeds its smoothing slider from this. */
  trace_bin_seconds?: number | null;
}

export interface MarkerLabelConfidence {
  /** 'low' means the trace should present this label as unverified
   *  rather than fact. */
  band: 'ok' | 'low';
  /** Which checks fired: empty, paraphrase_fallback, no_speech,
   *  low_logprob, repetitive. */
  reasons?: string[];
  no_speech_prob?: number | null;
  avg_logprob?: number | null;
  compression_ratio?: number | null;
}

export interface MarkerSegment {
  /** Session-elapsed seconds (same base as the chart x-axis). */
  start_s: number;
  end_s: number;
  text: string;
}

export interface MarkerLabel {
  /** 1-3 word phrase capturing the main activity / topic. */
  headline: string;
  /** One-sentence summary of the audio between the previous
   *  marker (or session t=0 for the first) and this marker. */
  summary: string;
  /** Raw Whisper transcript of the same interval. */
  transcript: string;
  /** Whisper's timestamped sub-segments for this window, in session-elapsed
   *  seconds — lets the caption track break a window into individual lines.
   *  Absent on labels scored before the densification. */
  segments?: MarkerSegment[] | null;
  /** AUDIO-LABEL-VALIDATION Phase 1: automatic reliability signal from
   *  Whisper (no-speech / logprob / repetition) + the paraphrase-
   *  fallback flag. Optional — labels scored before Phase 1 carry no
   *  confidence and are treated as unflagged. */
  confidence?: MarkerLabelConfidence | null;
}

export interface CognitiveTrace {
  window_s: number;
  step_s: number;
  focus: Array<number | null>;
  engagement: Array<number | null>;
  valence: Array<number | null>;
  workload: Array<number | null>;
  /** β_high / α — scored from cognitive-v1.1.0; absent on older traces. */
  stress?: Array<number | null>;
  /** (α + θ) / β — scored from cognitive-v1.1.0; absent on older traces. */
  fatigue?: Array<number | null>;
}

export interface CognitiveAverages {
  focus: number | null;
  engagement: number | null;
  valence: number | null;
  workload: number | null;
  session_count: number;
  avg_duration_seconds: number | null;
}

export interface CognitiveParticipantBreakdown {
  participant_user_id: string;
  participant_label: string | null;
  averages: CognitiveAverages;
}

/**
 * ROADMAP #44 faculty / mastery shape — surfaced by both cognitive
 * and motion tenant summaries when the picked procedure carries a
 * `procedure_group` and the master_baselines catalogue has a
 * matching row.
 */
export interface FacultyReference {
  cohort: string;
  procedure_group: string | null;
  metric: string;
  statistic: string;
  value: number;
  sample_size: number | null;
  source: string | null;
}

export interface MasteryRate {
  metric: string;
  faculty_value: number;
  /** 'higher_is_better' | 'lower_is_better' */
  direction: string;
  participants_at_or_better: number;
  participants_total: number;
}

export interface CognitiveTenantSummary {
  overall: CognitiveAverages;
  by_participant: CognitiveParticipantBreakdown[];
  procedures: string[];
  filter_procedure: string | null;
  /** Null when no procedure picked OR no master row exists for the
   *  picked procedure's group. */
  faculty_focus?: FacultyReference | null;
  mastery_rates?: MasteryRate[];
}

export async function listMyCognitiveScores(
  limit = 200,
  procedure?: string | null,
): Promise<CognitiveScore[]> {
  const params: Record<string, string | number> = { limit };
  if (procedure) params.procedure = procedure;
  const { data } = await apiClient.get<CognitiveScore[]>(
    '/api/v1/sessions/me/cognitive',
    { params },
  );
  return data;
}

export async function getTenantCognitiveSummary(
  tenantId: string,
  procedure?: string | null,
): Promise<CognitiveTenantSummary> {
  const params: Record<string, string> = {};
  if (procedure) params.procedure = procedure;
  const { data } = await apiClient.get<CognitiveTenantSummary>(
    `/api/v1/tenants/${tenantId}/cognitive`,
    { params },
  );
  return data;
}

export async function getSessionCognitiveDetail(
  tenantId: string,
  sessionId: string,
): Promise<CognitiveScore[]> {
  const { data } = await apiClient.get<CognitiveScore[]>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionId}/cognitive`,
  );
  return data;
}

export async function listParticipantCognitiveScores(
  tenantId: string,
  userId: string,
  limit = 200,
): Promise<CognitiveScore[]> {
  const { data } = await apiClient.get<CognitiveScore[]>(
    `/api/v1/tenants/${tenantId}/participants/${userId}/cognitive`,
    { params: { limit } },
  );
  return data;
}

/** Offered smoothing intervals (seconds) for the trace slider — mirrors the
 *  backend `TRACE_BIN_CHOICES`. 0 = the native 1-second trace. */
export const TRACE_BIN_CHOICES = [5, 10, 15, 20, 25, 30] as const;

export interface TraceRebinResult {
  session_id: string;
  trace_bin_seconds: number | null;
}

/** Re-bin a session's cognitive trace to an N-second smoothing interval
 *  (COGNITIVE-TRACE-SMOOTHING), tenant_admin only. Pass `null` (or 0) to
 *  reset to the native 1-second trace. Returns 202 — the recompute (markers,
 *  video frames, audio labels) runs async; poll the scores until
 *  `trace_bin_seconds` flips. */
export async function rebinCognitiveTrace(
  tenantId: string,
  sessionId: string,
  binSeconds: number | null,
): Promise<TraceRebinResult> {
  const { data } = await apiClient.post<TraceRebinResult>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionId}/cognitive-trace/rebin`,
    { bin_seconds: binSeconds },
  );
  return data;
}
